// src/components/tasks/TaskEditor.jsx

import firebase from "../../firebase/compat";
import { RRule } from "rrule";
import { RRuleEditor } from "./RruleEditor.jsx";
import SelectTeamMembers from "./SelectTeamMembers.jsx";
import { useEffect, useState, useContext, useMemo } from "react";
import {
  useFirestoreGeneral,
  useFirestoreUsersApproved,
  useFirestoreGeneralTwoOrderBy,
  useFirestoreGeneralOrderBy,
  useFirestoreTasksForMembers,
  useFirestorePersonaAbsences,
  useFirestorePersonaSchedules,
  useFirestoreUser,
} from "../../firebase/useFirestore.js";
import computeMemberAvailability from "../../helpers/availabilityHelpers/computeMemberAvailability.js";
import useSubtaskStatusChange from "../../hooks/useSubtaskStatusChange.js";
import getTeamMembers from "../../helpers/getTeamMembers.js";
import { getAdressLabels } from "../../helpers/getAdressDescription.js";
import { buildStreetLabelMap, streetLabel } from "../../helpers/adressHelpers/buildStreetLabels.js";
import { SavedIcon } from "../../StateManagment/SavedIcon.js";
import { Auth } from "../../StateManagment/Auth.js";
import UserRoleMeta from "../settings/UserRoleMeta.jsx";
import RouteMeta from "../personas/RouteMeta.jsx";
import SubTasks from "./SubTasks.jsx";
import TaskReminders from "./TaskReminders.jsx";
import Location from "../../hooks/Location.js";
import { db, timestamp } from "../../firebase/config.js";
import { client } from "../../hooks/Client.js";
import CopyLink from "../common/CopyLink.jsx";
import QrcodeIcon from "../../images/icons/qrcode-icon.png";
import { useHistory } from "react-router-dom";
import PresenceReportAdjuster from "./PresenceReportAdjuster.jsx";
import SelectableGrid from "../common/SelectableGrid.jsx";
import SubTasksTaskEditor from "./SubTasksTaskEditor.jsx";
import TaskSubtaskSummary from "./TaskSubtaskSummary.jsx";
import HouseholdMaterialsSection from "../adresses/materials/HouseholdMaterialsSection.jsx";
import HouseholdDataTable from "../adresses/data/HouseholdDataTable.jsx";
import HouseholdFixedDataCard from "../adresses/data/HouseholdFixedDataCard.jsx";
import HouseholdResearchLinks from "../adresses/research/HouseholdResearchLinks.jsx";
import useHouseholdParticipations from "../../hooks/useHouseholdParticipations";
import hasProgrammeQuestionnaire from "../../helpers/households/hasProgrammeQuestionnaire";
import householdResearchTabVisible from "../../helpers/households/householdResearchTabVisible";
import householdResearchTabCount from "../../helpers/households/householdResearchTabCount";
import useEffectiveResearchIds from "../../hooks/research/useEffectiveResearchIds";
import buildQuestionnaireShareLinks from "../../helpers/questionnaires/buildQuestionnaireShareLinks.js";
import recordStatusChange from "../../helpers/statusHistory/recordStatusChange.js";
import TaskNotes from "./TaskNotes.jsx";
import TaskImages from "./TaskImages.jsx";
import ScheduleOutputRegistration from './ScheduleOutputRegistration.jsx';
import AppointmentKindTitle from "./AppointmentKindTitle.jsx";
import { isAppointment } from "../../helpers/adressHelpers/taskKind";
import useAppointmentKinds from "../../hooks/useAppointmentKinds";
import { defaultVisitTitle } from "../../helpers/tasks/resolveAppointmentKinds";
import fetchHouseholdSubtasks from "../../helpers/adressHelpers/fetchHouseholdSubtasks";
import visitDurationMinutes from "../../helpers/tasks/visitDuration";
import useSettings from "../../hooks/Settings.jsx";
import ReadOnlyGuard from "../common/ReadOnlyGuard";
import buildPlanningSummary from "../../helpers/calendar/buildPlanningSummary";
import AdressStatusSelect from "../adresses/AdressStatusSelect.jsx";
import { statusDisplaysFrom } from "../../hooks/useStatusDisplay";
import autoMarkAdressCompleted from "../../helpers/adressHelpers/autoMarkAdressCompleted";
import { isVisitDone } from "../../helpers/adressHelpers/shouldAutoMarkCompleted";
import { ADRESS_STATUS_LABELS } from "../../helpers/adressHelpers/adressStatusLabels.js";
import getUserDisplayName from "../../helpers/getUserDisplayName.js";
import TaskHouseholdPicker from "./TaskHouseholdPicker.jsx";
import usePersonaTerms from '../../hooks/usePersonaTerms';

// Welke taaktypes aan een huishouden gekoppeld mogen worden. `teamSchedule`
// hoort er bewust NIET bij: koppelen zet het type om naar addressVisit, en dan
// verdwijnt het blok uit het rooster van die medewerker — zonder weg terug,
// want ontkoppelen levert `default` op.
const HOUSEHOLD_LINKABLE_TYPES = ["addressVisit", "default"];

const RRULE_DUTCH_WORDS = {
  every: "elke",
  day: "dag",
  days: "dagen",
  week: "week",
  weeks: "weken",
  month: "maand",
  months: "maanden",
  year: "jaar",
  years: "jaar",
  hour: "uur",
  hours: "uur",
  minute: "minuut",
  minutes: "minuten",
  on: "op",
  "on the": "op de",
  the: "de",
  Monday: "maandag",
  Tuesday: "dinsdag",
  Wednesday: "woensdag",
  Thursday: "donderdag",
  Friday: "vrijdag",
  Saturday: "zaterdag",
  Sunday: "zondag",
  weekday: "doordeweekse dag",
  weekdays: "doordeweekse dagen",
  and: "en",
  for: "voor",
  time: "keer",
  times: "keer",
  until: "tot",
  last: "laatste",
  first: "eerste",
  January: "januari",
  February: "februari",
  March: "maart",
  April: "april",
  May: "mei",
  June: "juni",
  July: "juli",
  August: "augustus",
  September: "september",
  October: "oktober",
  November: "november",
  December: "december",
};

// rrule's `weekdaytext` and `monthtext` read directly from `language.dayNames`
// / `language.monthNames` — they bypass the gettext callback. Without this
// override `toText` emits English weekday/month names regardless of the
// RRULE_DUTCH_WORDS lookup.
const RRULE_NL_LANGUAGE = {
  dayNames: ["zondag", "maandag", "dinsdag", "woensdag", "donderdag", "vrijdag", "zaterdag"],
  monthNames: ["januari", "februari", "maart", "april", "mei", "juni", "juli", "augustus", "september", "oktober", "november", "december"],
  tokens: {},
};

/**
 * @typedef {import('../../hooks/firebase/TaskDto.jsx').Task} Task
 * @typedef {'view' | 'create' | 'edit'} TaskEditorAction
 */

/**
 * Task editor component for creating, viewing, and editing tasks
 *
 * @param {object} props
 * @param {Task | null} props.task - The task being edited/viewed
 * @param {TaskEditorAction} props.action - Current action (view/create/edit)
 * @param {function(Task): void} props.onTaskChange - Callback when task changes (form inputs)
 * @param {function(Task): void} [props.onSave] - Callback when task is saved (submit button)
 * @param {function(): void} [props.onScheduleFollowUp] - Callback to schedule a follow-up visit (addressVisit only)
 */
const TaskEditor = ({ task, action, onTaskChange, onSave, onScheduleFollowUp, occurrenceDate }) => {
  // 'Medewerker' bij de FIXtool, 'Deelnemer' elders (verzoek BuurtKlusBedrijf 2026-09-28).
  const personaTerms = usePersonaTerms();
  // Context
  const [saved, setSaved] = useContext(SavedIcon);
  const [auth] = useContext(Auth);

  // Hooks
  const adressId = Location()[3];
  const settings = useSettings();
  const { adjustStatusInTaskEditor, autoCompleteHouseVisitStatus, impactFunctionality, adressStatusConfig } = settings;
  // De soorten afspraak van de organisatie: de titel uit een lijst, met
  // 'Anders…' voor vrije tekst. Alleen voor een huisbezoek of een gewone
  // afspraak; een roosterregel of meetmoment houdt zijn eigen titel.
  const kinds = useAppointmentKinds(settings);
  const kindsForTask = task && (isAppointment(task) || task.Type === "default" || !task.Type) ? kinds : [];
  // Waar de voorgestelde duur van een NIEUW huisbezoek vandaan komt: de nog in
  // te plannen klussen van het huishouden en hun geschatte minuten. Alleen
  // bij aanmaken; een bestaande afspraak heeft zijn duur al.
  // AI-CONTEXT: See docs/features/afspraak-soort-en-aanwezigheid-bkb.md
  const [timeNeed, setTimeNeed] = useState(/** @type {{count: number, minutes: number} | null} */ (null));
  const createAdressId = action === "create" ? (task?.AdressID || "") : "";
  useEffect(() => {
    let stale = false;
    if (!createAdressId) { setTimeNeed(null); return undefined; }
    fetchHouseholdSubtasks(createAdressId).then((subtasks) => {
      if (stale) return;
      const met = subtasks.filter((t) => Number(t.EstimateMinutes) > 0);
      setTimeNeed({ count: subtasks.length, minutes: met.length > 0 ? visitDurationMinutes(subtasks) : 0 });
    });
    return () => { stale = true; };
  }, [createAdressId]);
  // Meeliften op de useSettings hierboven: een eigen useStatusDisplays()
  // zou een tweede stel listeners op hetzelfde document opzetten.
  const statusDisplay = useMemo(
    () => statusDisplaysFrom({ adressStatusConfig }).adress,
    [adressStatusConfig],
  );

  const showAdressStatusSection =
    adjustStatusInTaskEditor && task?.Type === "addressVisit" && !!task?.AdressID;

  // ----------------------------
  // Tabs (controlled)
  // ----------------------------

  const TABS = useMemo(
    () => ({
      details: "details",
      household: "household",
      householdResearch: "householdResearch",
      subtasks: "subtasks",
      team: "team",
      materials: "materials",
      notes: "notes",
      images: "images",
      reminders: "reminders",
      link: "link",
      questionnaire: "questionnaire",
      attendance: "attendance",
      followup: "followup",
    }),
    []
  );

  const [activeTab, setActiveTab] = useState(TABS.details);

  const { runStatusChange } = useSubtaskStatusChange();

  useEffect(() => {
    setActiveTab(TABS.details);
  }, [action, TABS.details]);

  // ----------------------------
  // Firestore
  // ----------------------------

  const subTasks = useFirestoreGeneralTwoOrderBy(
    "Tasks",
    "AdressID",
    task?.AdressID || adressId || "",
    "Parent",
    task?.ID || null,
    "Position",
    "desc"
  );

  const measuremoment = useFirestoreGeneral(
    "MeasureMoments",
    "ID",
    task?.MeasureMomentID || ""
  );
  const research = useFirestoreGeneral(
    "Research",
    "ID",
    measuremoment[0]?.ResearchID || ""
  );
  const questionnaire = useFirestoreGeneral(
    "Questionnaires",
    "ID",
    research[0]?.QuestionnaireID || ""
  );

  const adressData = useFirestoreGeneralOrderBy(
    "PersonaData",
    "PersonaID",
    task?.Type === "addressVisit" ? (task?.AdressID || "") : "",
    "Position",
    "asc"
  );

  // Ook nodig buiten de statussectie: het tabblad Onderzoek leest de gekoppelde
  // onderzoeken van dit huishouden.
  const adressDoc = useFirestoreGeneral(
    "Adresses",
    "ID",
    task?.Type === "addressVisit" ? (task?.AdressID || "") : ""
  );
  const adressDocid = adressDoc?.[0]?.docid || "";
  // Hier en niet lager: onder een vroege return zou deze hook niet bij elke
  // render draaien, en dat is precies wat React niet toestaat.
  const participations = useHouseholdParticipations(adressDoc?.[0]?.HouseholdKey || "");
  const adressStatus = adressDoc?.[0]?.Status || "";
  // Adres van het huishouden: de vaste gegevens op het huishouden, tot de
  // migratie aangevuld uit de oude regels — dezelfde lezing als het overzicht.
  // AI-CONTEXT: See docs/features/huishouden-vaste-gegevens.md (T7)
  const adressLabels = buildStreetLabelMap(
    Array.isArray(adressData) ? adressData : [],
    adressDoc?.[0] ? [adressDoc[0]] : [],
  );
  const adressEntry = adressLabels[task?.AdressID] || {};
  const adressStreetLine = streetLabel(adressLabels, task?.AdressID, "");
  // De onderzoeken waaraan dit huishouden meedoet: handmatig gekoppeld plus
  // elk onderzoek dat voor alle huishoudens geldt. Alleen bij een huisbezoek
  // een listener; deze editor wordt voor elk soort afspraak gemount.
  // AI-CONTEXT: See docs/features/automatische-deelname-onderzoek.md
  const { ids: householdResearchIds, loaded: householdResearchLoaded } = useEffectiveResearchIds({
    kind: "adress",
    linkedIds: adressDoc?.[0]?.Research,
    enabled: task?.Type === "addressVisit" && !!task?.AdressID,
  });

  const adressStatusHandler = async (e) => {
    if (!adressDocid) return;
    const value = e.target.options[e.target.selectedIndex].value;
    await db.collection("Adresses").doc(adressDocid).update({ Status: value });
    await recordStatusChange({
      entityType: "adress",
      entityID: task?.AdressID,
      fromStatus: adressStatus,
      toStatus: value,
      changedBy: auth?.ID,
    });
    setSaved("flex");
  };

  const adressStatusSummary = adressStatus ? (ADRESS_STATUS_LABELS[adressStatus] || adressStatus) : "Geen status";

  // Een huisbezoek als afgerond markeren. Bewust een eigen handeling en geen
  // afleiding uit een datum die voorbij is of uit afgevinkte subtaken: een
  // bezoek waar niemand kwam opdagen ligt ook in het verleden.
  // AI-CONTEXT: See docs/features/afgerond-bezoek-status.md
  // `task` is een momentopname die bij het openen is meegegeven en niet
  // meebeweegt met Firestore -- de status van het huishouden ernaast komt wél
  // uit een live hook. Zonder deze eigen stand zou de knop na een klik blijven
  // zeggen 'Bezoek afronden' terwijl er 'Afgerond' naast staat: twee waarheden
  // op één scherm. Per docid, zodat een andere afspraak niet de stand van de
  // vorige erft. AI-CONTEXT: See docs/pitfalls.md#state-click-captured-firestore-snapshot
  const [visitDoneByDocid, setVisitDoneByDocid] = useState({});
  const visitDone = Object.prototype.hasOwnProperty.call(visitDoneByDocid, task?.docid)
    ? visitDoneByDocid[task.docid]
    : isVisitDone(task);
  const showVisitDone = task?.Type === "addressVisit" && !!task?.docid;

  const visitDoneHandler = async () => {
    if (!task?.docid) return;
    const next = !visitDone;
    setVisitDoneByDocid((prev) => ({ ...prev, [task.docid]: next }));

    await db.collection("Tasks").doc(task.docid).update({
      VisitDone: next,
      VisitDoneAt: next ? timestamp : null,
      VisitDoneBy: next ? (auth?.ID || "") : "",
    });

    // De instelling beslist óf het huishouden meebeweegt; de helper beslist of
    // het kán (alleen vooruit door de trechter). Alleen bij afronden, niet bij
    // terugdraaien: een status terugzetten is een beslissing van de planner.
    if (next && autoCompleteHouseVisitStatus) {
      await autoMarkAdressCompleted({ ...task, VisitDone: true }, auth?.ID);
    }

    setSaved("flex");
  };

  // Wie de afspraak heeft ingepland — elke aanmaakroute schrijft de auth-ID
  // naar `User`, dus dit is alleen nog een lookup.
  const plannedByUser = useFirestoreUser(task?.User || "");
  const plannedByName = getUserDisplayName(plannedByUser);

  // ----------------------------
  // Huishouden-koppeling
  // ----------------------------

  const showHouseholdSection =
    !!task && (!task.Type || HOUSEHOLD_LINKABLE_TYPES.includes(task.Type));

  const householdLabel = [
    adressStreetLine,
    adressEntry.Woonplaats,
  ]
    .filter(Boolean)
    .join(", ");

  const householdSummary = task?.AdressID
    ? householdLabel || "Gekoppeld huishouden"
    : "Geen huishouden";

  const householdHandler = async (nextAdressId) => {
    if (!task) return;

    if (!nextAdressId) {
      _onTaskChange({
        ...task,
        AdressID: null,
        AdressLabel: "",
        AdressName: "",
        Type: task.Type === "addressVisit" ? "default" : task.Type,
      });
      return;
    }

    // Een taak met een huishouden is een huisbezoek — dat stuurt de kleur in de
    // agenda en de bevestigingsknop in het huishoudenoverzicht. De andere
    // eigenaar-velden gaan leeg: een taak hoort bij één ding. Zelfde vorm als
    // CONTEXT_CONFIG.addressVisit.setFields in CalendarView.jsx.
    // Adres en bewonersnaam gaan mee op de taak, zodat de agenda ze zonder
    // tweede lookup in het blok kan zetten (één lezing, niet via de hook die
    // nog niet geladen kan zijn).
    // AI-CONTEXT: See docs/patterns.md#data-denormalise-label-onto-response
    const labels = await getAdressLabels(nextAdressId);
    _onTaskChange({
      ...task,
      AdressID: nextAdressId,
      AdressLabel: labels.adressLabel,
      AdressName: labels.adressName,
      // Inplannen vult de afspraak in: adres als beschrijving en de
      // standaardsoort als titel, maar nooit over eigen tekst heen.
      Description: String(task.Description || "").trim() ? task.Description : (labels.description || ""),
      ...(String(task.Title || "").trim() ? {} : defaultVisitTitle(kinds)),
      Type: "addressVisit",
      PersonaID: null,
      TeamMemberID: null,
      ActivityID: null,
      MeasureMomentID: null,
    });
  };

  const team = useFirestoreUsersApproved();
  const personas = useFirestoreGeneral("Personas", "Status", "active");
  const reminders = useFirestoreGeneralOrderBy(
    "TaskReminders",
    "TaskID",
    task?.ID || "",
    "Position",
    "asc"
  );

  const teamMembers = useMemo(() => getTeamMembers(team, personas), [team, personas]);

  const isAddressVisit = task?.Type === "addressVisit" && !!task?.AdressID;

  // Picker shows personas only — team users have no schedule/absences and
  // are out of scope for the availability feature.
  const pickerMembers = useMemo(
    () => (Array.isArray(teamMembers) ? teamMembers.filter((m) => m.Type === "persona") : []),
    [teamMembers]
  );

  // ----------------------------
  // Availability map for the team picker
  // ----------------------------

  const memberIds = useMemo(
    () => pickerMembers.map((m) => m.ID).filter(Boolean),
    [pickerMembers]
  );

  const availabilityWindow = useMemo(() => {
    if (!task?.Timestamp) return { start: null, end: null };
    const rawStart = task.Timestamp.toDate();
    if (task.TimestampEnd) {
      return { start: rawStart, end: task.TimestampEnd.toDate() };
    }
    // "Hele dag" — span the entire day of the start date.
    const start = new Date(rawStart);
    start.setHours(0, 0, 0, 0);
    const end = new Date(rawStart);
    end.setHours(23, 59, 59, 999);
    return { start, end };
  }, [task?.Timestamp, task?.TimestampEnd]);

  const tasksLookbackStart = useMemo(() => {
    if (!availabilityWindow.start) return null;
    const d = new Date(availabilityWindow.start);
    d.setFullYear(d.getFullYear() - 1);
    return d;
  }, [availabilityWindow.start]);

  const tasksForMembers = useFirestoreTasksForMembers(memberIds, client, tasksLookbackStart);
  const absencesForMembers = useFirestorePersonaAbsences(memberIds, client);
  const schedulesForMembers = useFirestorePersonaSchedules(memberIds, client);

  const availability = useMemo(
    () =>
      computeMemberAvailability({
        memberIds,
        tasks: tasksForMembers,
        absences: absencesForMembers,
        schedule: schedulesForMembers,
        currentTaskId: task?.docid || null,
        windowStart: availabilityWindow.start,
        windowEnd: availabilityWindow.end,
      }),
    [memberIds, tasksForMembers, absencesForMembers, schedulesForMembers, task?.docid, availabilityWindow]
  );

  // Hooks
  const history = useHistory();
  const researchID = task?.ResearchID || research[0]?.ID || "";
  const questionnaireID = task?.QuestionnaireID || questionnaire[0]?.ID || "";
  const measureMomentID = task?.MeasureMomentID || "";
  const personaOrUndefined = task?.PersonaID || "undefined";
  const selectedLanguage = "nl";

  /**
   * Converts a Firestore Timestamp to a `datetime-local` input value
   *
   * @param {firebase.firestore.Timestamp | null} timestamp
   * @returns {string}
   */
  const timestampToInputValue = (timestamp) => {
    if (!timestamp) return "";
    const date = timestamp.toDate();
    const offset = date.getTimezoneOffset() * 60000;
    return new Date(date.getTime() - offset).toISOString().slice(0, 16);
  };

  /**
   * Converts a `datetime-local` input value to a Firestore Timestamp
   *
   * @param {string} value
   * @returns {firebase.firestore.Timestamp | null}
   */
  const inputValueToTimestamp = (value) => {
    return value ? firebase.firestore.Timestamp.fromDate(new Date(value)) : null;
  };

  const _onTaskChange = async (nextTask) => {
    onTaskChange(nextTask);
  };

  const pad2 = (n) => String(n).padStart(2, "0");

  const dateToHHmm = (d) => {
    if (!d) return "";
    return `${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
  };

  // decide where personaId lives in your task model:
  const taskPersonaId =
    task?.PersonaID ||
    task?.Personas?.[0] || // if some tasks store Personas array
    null;

  const scheduledStart = task?.Timestamp ? dateToHHmm(task.Timestamp.toDate()) : "";
  const scheduledEnd = task?.TimestampEnd ? dateToHHmm(task.TimestampEnd.toDate()) : "";

  // Helper to get member role name
  const memberRole = (member) => {
    switch (member.Type) {
      case "team":
        return <UserRoleMeta userRoleId={member.Role} />;
      case "persona":
        return <RouteMeta routeId={member.Role} />;
      default:
        return "Geen rol gevonden";
    }
  };

  // Subtask status handler — delegates stock deduction/restoration to the shared hook.
  const subTaskStatusHandler = async (e) => {
    const docid = e.target.dataset.docid;
    const newStatus = e.target.options[e.target.selectedIndex].value;

    const subtask = (subTasks || []).find((s) => s.docid === docid);
    const { ok } = await runStatusChange(subtask, newStatus);
    if (ok) setSaved("flex");
  };

  /* -------------------------------------------------------
   * Collapsible summaries ("glimpse" when collapsed)
   * ----------------------------------------------------- */

  const safeRRuleText = (rruleString) => {
    if (!rruleString) return "";
    try {
      const rule = RRule.fromString(rruleString);
      return rule.toText((id) => RRULE_DUTCH_WORDS[id] || id, RRULE_NL_LANGUAGE);
    } catch (e) {
      return "Aangepaste herhaling";
    }
  };

  // De agenda klapt een herhaalreeks uit in losse dagen; het document draagt de
  // datum van de eerste keer. Daarom gaat de aangeklikte dag apart mee naar
  // buildPlanningSummary — anders zegt de kopregel bij een afspraak in september
  // '07-01' en denkt de gebruiker dat hij het verkeerde item open heeft.
  // AI-CONTEXT: See docs/pitfalls.md#state-recurring-event-wrong-date
  const planningSummary = useMemo(
    () => buildPlanningSummary({
      task,
      occurrenceDate,
      repeatText: task?.rrule ? safeRRuleText(task.rrule) : "",
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [task, occurrenceDate]
  );
  const subtasksCount = subTasks?.length || 0;
  const personaMemberIds = useMemo(
    () =>
      (task?.TeamMembers || []).filter((id) => {
        const m = teamMembers?.find((mem) => mem.ID === id);
        return m?.Type === "persona";
      }),
    [task, teamMembers]
  );
  const personaCount = personaMemberIds.length;
  const remindersCount = Array.isArray(reminders) ? reminders.length : 0;

  // ----------------------------
  // IMPORTANT FIX:
  // Avoid infinite update loop when opening "Inplannen"
  // - Only call _onTaskChange if rrule/exdate actually changed
  // - Provide a stable dtstart Date
  // ----------------------------

  const dtstartStable = useMemo(() => {
    if (!task?.Timestamp) return null;
    try {
      return new Date(task.Timestamp.toMillis());
    } catch (e) {
      return task.Timestamp.toDate();
    }
  }, [task?.Timestamp]);

  const arraysEqual = (a, b) => {
    const aa = Array.isArray(a) ? a : [];
    const bb = Array.isArray(b) ? b : [];
    if (aa.length !== bb.length) return false;
    for (let i = 0; i < aa.length; i++) {
      if (aa[i] !== bb[i]) return false;
    }
    return true;
  };

  const handleRRuleChange = ({ rrule, exdate }) => {
    if (!task) return;

    const nextRrule = rrule ? rrule.toString() : null;
    const nextExdate = Array.isArray(exdate) ? exdate : [];

    const prevRrule = task.rrule || null;
    const prevExdate = Array.isArray(task.exdate) ? task.exdate : [];

    const sameRrule = prevRrule === nextRrule;
    const sameExdate = arraysEqual(prevExdate, nextExdate);

    if (sameRrule && sameExdate) return;

    _onTaskChange({
      ...task,
      rrule: nextRrule,
      exdate: nextExdate,
      exrule: null,
    });
  };

  if (!task) return <div className="task-editor" />;

  const isEditing = action === "edit" || action === "create";

  const showLinkTab = task.Type === "activityForm";
  const showQuestionnaireTab = task.Type === "measureMoment";
  const showAttendanceTab = task.Type === "personaSchedule" && !!taskPersonaId;
  const showMaterialsTab = isAddressVisit;
  // Alle gegevens van het huishouden, dezelfde tabel als op de huishoudenpagina.
  const showHouseholdDataTab = isAddressVisit;
  // Onderzoek-tab bij ELK huisbezoek, ook zonder gekoppeld onderzoek: het
  // tabblad zegt dan zelf dat er nog niets hangt en waar je dat regelt.
  // Het aantal achter de naam telt twee bronnen: de eigen onderzoeken waaraan
  // dit huishouden meedoet, én de vragenlijst van het programma waar het in
  // meedraait (die staat in een andere omgeving en komt via de deelname-rij).
  // AI-CONTEXT: See docs/pitfalls.md#dev-second-source-never-reaches-its-own-gate
  const heeftProgrammaVragenlijst = hasProgrammeQuestionnaire(participations);
  const showHouseholdResearchTab = householdResearchTabVisible({ isAddressVisit, impactFunctionality });
  const showFollowupTab =
    task.Type === "addressVisit" && !!onScheduleFollowUp && !isEditing;

  const tabsDef = [
    { id: TABS.details, label: "Details" },
    showHouseholdDataTab && { id: TABS.household, label: "Gegevens" },
    { id: TABS.subtasks, label: "Subtaken", count: subtasksCount || null },
    { id: TABS.team, label: "Medewerkers", count: personaCount || null },
    showMaterialsTab && { id: TABS.materials, label: "Materialen" },
    { id: TABS.notes, label: "Notities" },
    { id: TABS.images, label: "Foto's" },
    { id: TABS.reminders, label: "Herinneringen", count: remindersCount || null },
    showLinkTab && { id: TABS.link, label: "Link" },
    showQuestionnaireTab && { id: TABS.questionnaire, label: "Vragenlijst" },
    showAttendanceTab && { id: TABS.attendance, label: "Aanwezigheid" },
    showFollowupTab && { id: TABS.followup, label: "Vervolgbezoek" },
    showHouseholdResearchTab && {
      id: TABS.householdResearch,
      label: "Onderzoek",
      count: householdResearchTabCount({
        ids: householdResearchIds,
        loaded: householdResearchLoaded,
        hasProgramme: heeftProgrammaVragenlijst,
      }),
    },
  ].filter(Boolean);

  const detailsView = (
    <div className="task-editor-details-grid">
      <div className="task-editor-field">
        <span className="task-editor-field-label">Titel</span>
        <span className="task-editor-field-value">{task.Title || "—"}</span>
      </div>
      <div className="task-editor-field">
        <span className="task-editor-field-label">Beschrijving</span>
        <span className="task-editor-field-value">{task.Description || "—"}</span>
      </div>
      {showHouseholdSection && (
        <div className="task-editor-field">
          <span className="task-editor-field-label">Huishouden</span>
          <span className="task-editor-field-value">{householdSummary}</span>
        </div>
      )}
      {showAdressStatusSection && (
        <div className="task-editor-field">
          <span className="task-editor-field-label">Status huishouden</span>
          <span className="task-editor-field-value">{adressStatusSummary}</span>
        </div>
      )}
      {showVisitDone && (
        <div className="task-editor-field">
          <span className="task-editor-field-label">Bezoek</span>
          <span className="task-editor-field-value">
            {visitDone ? "Afgerond" : "Nog niet afgerond"}
          </span>
        </div>
      )}
      {task.User && (
        <div className="task-editor-field">
          <span className="task-editor-field-label">Ingepland door</span>
          <span className="task-editor-field-value">
            {plannedByName || "Onbekende gebruiker"}
          </span>
        </div>
      )}
      {/* Bij een herhalende afspraak is Timestamp de EERSTE keer, niet de dag
          die je aanklikte — die staat in de kopregel hierboven. 'Startdatum'
          zou daar dus liegen; 'Eerste keer' klopt wel. TimestampEnd is de
          eindtijd van diezelfde eerste keer en niet het einde van de reeks:
          als 'Einddatum' leest dat als 'hierna stopt het', wat onwaar is. De
          tijden staan al in de kopregel, dus dat veld vervalt bij herhaling. */}
      {task.Timestamp && (
        <div className="task-editor-field">
          <span className="task-editor-field-label">
            {task.rrule ? "Eerste keer" : "Startdatum"}
          </span>
          <span className="task-editor-field-value">
            {task.Timestamp.toDate().toLocaleString("nl-NL")}
          </span>
        </div>
      )}
      {task.TimestampEnd && !task.rrule && (
        <div className="task-editor-field">
          <span className="task-editor-field-label">Einddatum</span>
          <span className="task-editor-field-value">
            {task.TimestampEnd.toDate().toLocaleString("nl-NL")}
          </span>
        </div>
      )}
      {task.rrule && (
        <div className="task-editor-field">
          <span className="task-editor-field-label">Herhaalt</span>
          <span className="task-editor-field-value">{safeRRuleText(task.rrule)}</span>
        </div>
      )}
      {task.Tags && task.Tags.length > 0 && (
        <div className="task-editor-field">
          <span className="task-editor-field-label">Tags</span>
          <span className="task-editor-field-value">{task.Tags.join(", ")}</span>
        </div>
      )}
      {/* Updateverzoek BuurtKlusBedrijf: de subtaken stonden alleen op hun eigen
          tabblad, dus je moest wisselen om te zien wat er nog lag. Hier alleen
          lezen — aanpassen blijft op dat tabblad, zodat er één plek is die
          schrijft. */}
      <TaskSubtaskSummary subTasks={subTasks} />
    </div>
  );

  const linkPanel = (
    <div>
      <div className="task-details-activityform-send-container">
        <p><b>Link:</b></p>
        <CopyLink link={`/${client}/eventopen/${task.ActivityFormID}/open`} />
      </div>
      <div className="task-details-activityform-send-container">
        <p><b>QR-code:</b></p>
        <img
          src={QrcodeIcon}
          alt=""
          className="table-delete-icon"
          onClick={() => history.push(`/${client}/qrcode/${task.ActivityFormID}/event`)}
        />
      </div>
    </div>
  );

  const questionnaireLinks = buildQuestionnaireShareLinks({
    client,
    questionnaireID,
    measureMomentID,
    researchID,
    respondentID: personaOrUndefined,
    language: selectedLanguage,
  });

  const questionnairePanel = questionnaireLinks ? (
    <div>
      <div className="task-details-activityform-send-container">
        <p><b>Vragenlijst versturen</b></p>
        <CopyLink link={questionnaireLinks.open} />
      </div>
      <div className="task-details-activityform-send-container">
        <p><b>QR-code:</b></p>
        <img
          src={QrcodeIcon}
          id="share-questionnaire-qrcode-icon"
          alt=""
          onClick={() => history.push(questionnaireLinks.qrcode)}
        />
      </div>
    </div>
  ) : (
    <p>De vragenlijst van dit meetmoment kon niet worden gevonden.</p>
  );

  const materialsPanel = (
    // Alleen de materialen van deze afspraak; wat je hier toevoegt hangt eraan.
    // AI-CONTEXT: See docs/features/household-materials.md (Uitbreiding 2026-10, M4)
    <HouseholdMaterialsSection adressID={task.AdressID} showPrices={false} taskID={task.ID} />
  );

  const householdDataPanel = (
    <div className="task-editor-household-data">
      {/* Vaste gegevens staan niet meer in de tabel, dus hier ook de kaart.
          AI-CONTEXT: See docs/features/huishouden-vaste-gegevens.md (T4) */}
      <HouseholdFixedDataCard adressID={task.AdressID} />
      <HouseholdDataTable adressID={task.AdressID} />
    </div>
  );

  const householdResearchPanel = <HouseholdResearchLinks adressID={task.AdressID} />;

  // AI-CONTEXT: See docs/pitfalls.md#state-recurring-event-wrong-date
  // Bij een herhalende afspraak is Timestamp de datum van de EERSTE keer; de
  // datum van dit item in de agenda komt via occurrenceDate.
  const attendanceDate =
    occurrenceDate || (task?.Timestamp ? task.Timestamp.toDate() : new Date());

  const attendancePanel = (
    <>
      <PresenceReportAdjuster
        scheduleId={task?.docid || ""}
        personaId={taskPersonaId}
        scheduledStart={scheduledStart}
        scheduledEnd={scheduledEnd}
        taskDate={attendanceDate}
        onSaved={() => setSaved("flex")}
      />
      <ScheduleOutputRegistration
        scheduleId={task?.docid || ""}
        personaId={taskPersonaId}
        outputId={task?.OutputID || ""}
        taskDate={attendanceDate}
        onSaved={() => setSaved("flex")}
      />
    </>
  );

  const followupPanel = (
    <div className="task-editor-followup-container">
      <button className="task-editor-followup-button" onClick={onScheduleFollowUp}>
        Vervolgbezoek plannen
      </button>
    </div>
  );

  const detailsForm = (
    <div>
      <AppointmentKindTitle
        title={task.Title}
        kinds={kindsForTask}
        onChange={(next) => _onTaskChange({ ...task, ...next })}
      />

      <div>
        <label>
          <strong>Beschrijving</strong>
          <textarea
            defaultValue={task.Description}
            onBlur={(e) => _onTaskChange({ ...task, Description: e.target.value })}
            rows={4}
            className="task-editor-textarea"
          />
        </label>
      </div>

      {showHouseholdSection && (
        <div>
          <label>
            <strong>Huishouden</strong>
          </label>
          <TaskHouseholdPicker value={task.AdressID} onChange={householdHandler} />
          <p className="task-editor-household-hint">
            Een afspraak koppelen aan een huishouden maakt er een huisbezoek van.
          </p>
        </div>
      )}

      <div>
        <label>
          <strong>Starttijd</strong>
          <input
            type="datetime-local"
            value={timestampToInputValue(task.Timestamp)}
            onChange={(e) => _onTaskChange({ ...task, Timestamp: inputValueToTimestamp(e.target.value) })}
            className="task-editor-input"
          />
        </label>
      </div>

      {task.TimestampEnd && (
        <div>
          <label>
            <strong>Eindtijd</strong>
            <input
              type="datetime-local"
              value={timestampToInputValue(task.TimestampEnd)}
              onChange={(e) => _onTaskChange({ ...task, TimestampEnd: inputValueToTimestamp(e.target.value) })}
              className="task-editor-input"
            />
          </label>
        </div>
      )}

      {timeNeed && timeNeed.count > 0 && (
        <p className="task-editor-household-hint">
          {timeNeed.count === 1 ? '1 klus' : `${timeNeed.count} klussen`} nog in te plannen bij dit huishouden
          {timeNeed.minutes > 0 ? `, ${timeNeed.minutes} minuten geschat` : ', zonder schatting'}
        </p>
      )}

      <div className="task-editor-row">
        <label className="task-editor-checkbox">
          <input
            type="checkbox"
            checked={!task.TimestampEnd}
            onChange={(e) =>
              _onTaskChange({
                ...task,
                TimestampEnd: e.target.checked ? null : task.Timestamp,
              })
            }
          />
          Hele dag
        </label>
      </div>

      <RRuleEditor
        rrule={task.rrule}
        dtstart={dtstartStable}
        exdate={task.exdate}
        onChange={handleRRuleChange}
      />

      {showAdressStatusSection && (
        <div>
          <label>
            <strong className="task-editor-status-label">Status huishouden</strong>
            <AdressStatusSelect
              value={adressStatus}
              display={statusDisplay}
              onChange={adressStatusHandler}
              className="task-editor-input"
            />
          </label>
        </div>
      )}
      {showVisitDone && (
        <div className="task-editor-visit-done">
          <button type="button" onClick={visitDoneHandler}>
            {visitDone ? "Afronden ongedaan maken" : "Bezoek afronden"}
          </button>
        </div>
      )}
    </div>
  );

  const teamView =
    personaMemberIds.length > 0 ? (
      <ul>
        {personaMemberIds.map((memberId) => {
          const member = teamMembers.find((m) => m.ID === memberId);
          return (
            <li key={memberId}>
              {member.Name} - {memberRole(member)}
            </li>
          );
        })}
      </ul>
    ) : (
      <p>Geen {personaTerms.pluralLower}</p>
    );

  const panel = (() => {
    switch (activeTab) {
      case TABS.details:
        return isEditing ? detailsForm : detailsView;
      case TABS.household:
        return showHouseholdDataTab ? householdDataPanel : null;
      case TABS.subtasks:
        return isEditing ? (
          <SubTasks task={task} subTasks={subTasks} />
        ) : (
          <SubTasksTaskEditor subTasks={subTasks} subTaskStatusHandler={subTaskStatusHandler} />
        );
      case TABS.team:
        return isEditing ? (
          <SelectTeamMembers
            taskDocid={task?.docid || ""}
            teamMembers={pickerMembers}
            selectedIds={task?.TeamMembers || []}
            onChange={(nextIds) => _onTaskChange({ ...task, TeamMembers: nextIds })}
            memberRole={memberRole}
            onSaved={() => setSaved("flex")}
            availability={availability}
          />
        ) : (
          teamView
        );
      case TABS.materials:
        return showMaterialsTab ? materialsPanel : null;
      case TABS.notes:
        return <TaskNotes task={task} />;
      case TABS.images:
        return <TaskImages task={task} />;
      case TABS.reminders:
        return <TaskReminders task={task} recipients={teamMembers} reminders={reminders} />;
      case TABS.link:
        return showLinkTab ? linkPanel : null;
      case TABS.questionnaire:
        return showQuestionnaireTab ? questionnairePanel : null;
      case TABS.attendance:
        return showAttendanceTab ? attendancePanel : null;
      case TABS.followup:
        return showFollowupTab ? followupPanel : null;
      case TABS.householdResearch:
        return showHouseholdResearchTab ? householdResearchPanel : null;
      default:
        return null;
    }
  })();

  const body = (
    <>
      {action === "view" && task.Type === "addressVisit" && (
        <div className="task-editor-adress-header">
          <p>{adressStreetLine}</p>
          <p>{adressEntry.Postcode} {adressEntry.Woonplaats}</p>
        </div>
      )}

      {planningSummary && (
        <div className="task-editor-pills">
          <span className="task-editor-pill">{planningSummary}</span>
        </div>
      )}

      {task.User && (
        <p className="task-editor-planned-by">
          <strong>Ingepland door:</strong> {plannedByName || "Onbekende gebruiker"}
        </p>
      )}

      <SelectableGrid
        items={tabsDef}
        getId={(item) => item.id}
        getLabel={(item) => item.label}
        selectedId={activeTab}
        onClick={setActiveTab}
      />

      <div className="task-editor-tab-panel">{panel}</div>
    </>
  );

  return (
    <div className="task-editor">
      {isEditing ? (
        <ReadOnlyGuard edit="tasks.edit">
          <form
            onSubmit={async (e) => {
              e.preventDefault();
              if (onSave) onSave(task);
            }}
          >
            {body}
            {onSave && (
              <div id="modal-button-container">
                <button id="modal-save-button" type="submit">
                  Opslaan
                </button>
              </div>
            )}
          </form>
        </ReadOnlyGuard>
      ) : (
        body
      )}
    </div>
  );
};

export default TaskEditor;
